'use client';

import { c as _c } from "react/compiler-runtime";
import { AVATAR_EDITOR_GROUPS, AVATAR_VARIANT_NONE, type AvatarEditorGroup, avatarGroupChoices } from '@shared/all/domains/avatar';
import { buildAvatarUrl } from '@shared/all/helpers/avatarUrl';
import { API_URL } from '@shared/utils/helpers/config';
import { useAvatarStyle } from '@shared/utils/hooks/useAvatarStyle';
import { Dices, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
type Props = {
  /** The current avatar spec — the `robohash` value. See domains/avatar.ts. */
  spec: string | null;
  closeHandler: () => void;
  onSave: (spec: string) => void;
};

/**
 * Lets a verified human choose how their generated avatar looks.
 *
 * Every swatch is a real render of the avatar with that one value swapped, not
 * an icon or a name — "sideSwept" and "shoulderLength" mean nothing until you
 * see them on your own face. That is affordable because the swatches come from
 * our own endpoint: each is ~1.5 KB of SVG, immutable, and every viewer of the
 * app shares the same cache entries, so the second person to open the hair
 * group pays nothing for it.
 *
 * Only the open group renders swatches. All sixteen at once would be about 130
 * requests on open, most of them never looked at.
 */
const AvatarStyleEditor = t0 => {
  const $ = _c(45);
  const {
    spec,
    closeHandler,
    onSave
  } = t0;
  const style = useAvatarStyle(spec);
  const [openGroup, setOpenGroup] = useState(AVATAR_EDITOR_GROUPS[0]);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2>Customise your avatar</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== closeHandler) {
    t2 = <ModalHeader closeHandler={closeHandler}>{t1}</ModalHeader>;
    $[1] = closeHandler;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== style.spec) {
    t3 = buildAvatarUrl(API_URL, style.spec);
    $[3] = style.spec;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <img src={t3} alt="Your avatar" width={128} height={128} className="rounded-full" />;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Dices height={15} width={15} />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== style.shuffle) {
    t6 = <Button variant="CLEAR" onClick={style.shuffle} icon={t5} tooltip="Shuffle everything you haven't chosen" />;
    $[8] = style.shuffle;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = !style.hasChoices;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <RotateCcw height={15} width={15} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== style.reset || $[12] !== t7) {
    t9 = <Button variant="CLEAR" onClick={style.reset} disabled={t7} icon={t8} tooltip="Clear your choices" />;
    $[11] = style.reset;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t9) {
    t10 = <div className="flex gap-1">{t6}{t9}</div>;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t4) {
    t11 = <div className="flex shrink-0 flex-col items-center gap-2">{t4}{t10}</div>;
    $[17] = t10;
    $[18] = t4;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== openGroup?.name || $[21] !== style) {
    t12 = AVATAR_EDITOR_GROUPS.map(group => {
      const isOpen = openGroup?.name === group.name;
      const chosen = style.options[group.name];
      return <div key={group.name} className="rounded-md border border-ds-border-standard"><button type="button" onClick={() => setOpenGroup(isOpen ? null : group)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm" aria-expanded={isOpen}><span className="font-medium text-textPrimary">{group.label}</span><span className="text-xs text-textSecondary">{chosen ?? "From your seed"}</span></button>{isOpen && <div className="flex flex-wrap gap-2 border-t border-ds-border-standard p-3">{chosen !== undefined && <button type="button" onClick={() => style.clearGroup(group)} className="rounded-md border border-ds-border-standard px-2 py-1 text-xs text-textSecondary hover:border-primary">From your seed</button>}{avatarGroupChoices(group).map(value => group.kind === "palette" ? <button key={value} type="button" aria-label={`${group.label} ${value}`} onClick={() => style.choose(group, value)} style={{
            backgroundColor: `#${value}`
          }} className={`h-8 w-8 rounded-full border-2 ${chosen === value ? "border-primary" : "border-transparent"}`} /> : <button key={value} type="button" aria-label={`${group.label} ${value}`} onClick={() => style.choose(group, value)} className={`overflow-hidden rounded-full border-2 ${chosen === value ? "border-primary" : "border-transparent"}`}>{value === AVATAR_VARIANT_NONE ? <span className="flex h-12 w-12 items-center justify-center bg-background-secondary text-[10px] text-textSecondary">None</span> : <img src={buildAvatarUrl(API_URL, style.previewSpec(group, value))} alt="" width={48} height={48} loading="lazy" />}</button>)}</div>}</div>;
    });
    $[20] = openGroup?.name;
    $[21] = style;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12) {
    t13 = <div className="max-h-[420px] flex-1 space-y-1 overflow-y-auto pr-1">{t12}</div>;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t13) {
    t14 = <ModalContent><div className="flex gap-4">{t11}{t13}</div></ModalContent>;
    $[25] = t11;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== closeHandler) {
    t15 = <Button text="Cancel" variant="CLEAR" onClick={closeHandler} />;
    $[28] = closeHandler;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== closeHandler || $[31] !== onSave || $[32] !== style.spec) {
    t16 = () => {
      onSave(style.spec);
      closeHandler();
    };
    $[30] = closeHandler;
    $[31] = onSave;
    $[32] = style.spec;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = !style.isDirty;
  let t18;
  if ($[34] !== t16 || $[35] !== t17) {
    t18 = <Button text="Use this avatar" onClick={t16} disabled={t17} />;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t15 || $[38] !== t18) {
    t19 = <ModalFooter>{t15}{t18}</ModalFooter>;
    $[37] = t15;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== closeHandler || $[41] !== t14 || $[42] !== t19 || $[43] !== t2) {
    t20 = <Modal closeHandler={closeHandler} maxWidth={640}>{t2}{t14}{t19}</Modal>;
    $[40] = closeHandler;
    $[41] = t14;
    $[42] = t19;
    $[43] = t2;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
};
export default AvatarStyleEditor;